import React, {createContext, useContext, useEffect, useMemo, useReducer} from 'react';
import {ActionType} from 'typesafe-actions';
import {ProductsActionTypes, ProductsActions} from './ProductsActionTypes';
import {useParams} from 'react-router-dom';
import {
  DesignerSettingsCurrency,
  LocaleSettings,
  LogoSettings,
  ProductThemingSettings,
  TaopixCore,
  UrlSettings,
} from '@taopix/taopix-core/dist/types/types';
import {Lang, ProjectPreviewMode, ThemingProjectCreateMode} from '@taopix/taopix-core';
import i18n from '../../../i18n/i18n';
import useLangCode from '../../../Hooks/useLangCode';

export type Collection = {
  category: string;
  categoryCode: string;
  collectionCode: string;
  collectionDescription: string;
  collectionName: string;
  collectionUniqueRef: string;
  newPreviewImage: string;
  newThumbnailImage: string;
  previewImageDevicePixelRatio: number;
  sortLevel: number;
  thumbnailImageDevicePixelRatio: number;
  type: string;
  upgradeLayoutPreviewImage: string;
  thumbnailPath: string;
  thumbpath: string;
  imageBasePath: string;
  products: Record<number, any>;
};

export type SelectorData = {
  collections: Collection[];
  baseImagePath: string;
  baseThumbnailPath: string;
  theme: string;
};

export type RootAction = ActionType<typeof import('./ProductsActionTypes')>;

export type ProductsState = {
  selector: SelectorData;
  loading: boolean;
  loaded: boolean;
  locale: LocaleSettings;
  logo: LogoSettings;
  urls: UrlSettings;
  currency: DesignerSettingsCurrency;
  productTheming: ProductThemingSettings;
};

export type ProductsContextState = {
  state: ProductsState;
  dispatch: React.Dispatch<RootAction>;
};

const initialState: ProductsState = {
  selector: {
    collections: [],
    baseImagePath: '',
    baseThumbnailPath: '',
    theme: '',
  },
  loaded: false,
  loading: false,
  locale: {
    available: {} as any,
    defaultLanguage: 'en',
    language: 'en',
    strings: {} as any,
    orderButtonString: '',
    orderButtonTranslations: '',
  },
  logo: {
    image: '',
    imageDark: '',
    linkURL: '',
    tip: '',
  },
  urls: {
    callbackUrl: '',
    cdnURL: '',
    imagePath: '',
    thumbnailPath: '',
    serverURL: '',
    help: '',
    about: '',
    terms: '',
  },
  currency: {
    code: '',
    name: '',
    isoNumber: '',
    symbol: '',
    symbolAtFront: true,
    decimalPlaces: 0,
    exchangeRate: '1.0000',
  },
  productTheming: {
    enabled: true,
    livePreviewsEnabled: false,
    createUrlFormat: '',
    editUrlFormat: '',
    deleteUrlFormat: '',
    livePreviewMode: ProjectPreviewMode.Flip,
    createMode: ThemingProjectCreateMode.NATIVE,
  },
};

export const ProductsContext = createContext<ProductsContextState>({} as ProductsContextState);

const ProductsReducer = (state: ProductsState, action: RootAction): ProductsState => {
  switch (action.type) {
    case ProductsActionTypes.SET_SELECTOR_DATA: {
      return {
        ...state,
        selector: action.payload,
      };
    }
    case ProductsActionTypes.SET_LOADING: {
      return {
        ...state,
        loading: action.payload,
      };
    }
    case ProductsActionTypes.SET_DATA_LOADED: {
      return {
        ...state,
        loaded: action.payload,
      };
    }
    case ProductsActionTypes.SET_CURRENCY_SETTINGS: {
      return {
        ...state,
        currency: action.payload,
      };
    }
    case ProductsActionTypes.SET_LOCALE_SETTINGS: {
      return {
        ...state,
        locale: action.payload,
      };
    }
    case ProductsActionTypes.SET_LOGO_SETTINGS: {
      return {
        ...state,
        logo: action.payload,
      };
    }
    case ProductsActionTypes.SET_URL_SETTINGS: {
      return {
        ...state,
        urls: action.payload,
      };
    }
    case ProductsActionTypes.SET_THEMING_SETTINGS: {
      return {
        ...state,
        productTheming: action.payload,
      };
    }
    default:
      return state;
  }
};

export const ProductsProvider = (props: {children: any; core: TaopixCore}) => {
  const {children, core} = props;
  const [state, dispatch] = useReducer(ProductsReducer, {
    ...initialState,
  });

  let {groupCode} = useParams();
  const langCode = useLangCode();

  // give the groupCode a quick sanitise if licencee's create a bad URL
  const matches = groupCode?.match(/[&]/g);
  if (matches && matches.length) {
    groupCode = groupCode?.split(matches[0])[0];
  }

  useEffect(() => {
    dispatch(ProductsActions.setLoading(true));

    fetch(`${window.config.baseURL}/api/product/collections`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        key: window.config.pubKey,
        groupCode,
        langCode,
      }),
    }).then(payload => {
      payload.json().then(data => {
        dispatch(ProductsActions.setSelectorData(data.selector));
        dispatch(ProductsActions.setCurrencySettings(data.currency));
        dispatch(ProductsActions.setUrlSettings(data.urls));
        dispatch(ProductsActions.setLocaleSettings(data.locale));
        dispatch(ProductsActions.setLogoSettings(data.logo));
        dispatch(ProductsActions.setThemingSettings(data.productTheming));
        dispatch(ProductsActions.setLoading(false));
        dispatch(ProductsActions.setDataLoaded(true));

        /* Respond to the language sent by backend */
        i18n.changeLanguage(data.locale.language).then(() => {
          localStorage.setItem('i18nextLng', data.locale.language);
          Lang.setCode(data.locale.language);
        });

        // set the theme && base url
        core.addOptions({
          theme: data.selector.theme,
          baseURL: data.urls.serverURL,
        });
      });
    });
  }, [core]);

  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state, dispatch]
  );

  return <ProductsContext.Provider value={contextValue}>{children}</ProductsContext.Provider>;
};

export const useProductsContext = () => {
  const context = useContext(ProductsContext);
  if (context === undefined) {
    throw new Error('useProductsContext must be used within a ProductsProvider');
  }
  return context;
};
